import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'
import type { Book } from '@/types/book'
import type { RouletteWithStats } from '@/types/roulette'

interface RouletteHistoryProps {
  rouletteId: string
  onBackToRoulette: () => void
  onSpinRoulette?: (roulette: RouletteWithStats) => void
}

interface RouletteSpinHistory {
  id: string
  selected_book_id: string
  spin_duration: number
  spun_at: string
  books: Book
}

export function RouletteHistory({
  rouletteId,
  onBackToRoulette,
  onSpinRoulette,
}: RouletteHistoryProps) {
  const { t } = useTranslation()
  const { user } = useAuth()

  // Fetch roulette details
  const { data: roulette } = useQuery({
    queryKey: ['roulette', rouletteId, user?.id],
    queryFn: async (): Promise<RouletteWithStats> => {
      if (!user?.id || !rouletteId) throw new Error('Missing parameters')

      const { data, error } = await supabase
        .from('roulettes')
        .select(`
          *,
          roulette_books (
            books (
              id,
              title,
              author,
              cover_url,
              status,
              rating
            )
          ),
          roulette_spins (
            id,
            selected_book_id,
            spun_at
          )
        `)
        .eq('id', rouletteId)
        .eq('user_id', user.id)
        .single()

      if (error) throw error

      const books =
        data.roulette_books?.map((rb: { books: Book }) => rb.books).filter(Boolean) || []
      const roulette_spins = data.roulette_spins || []

      return {
        ...data,
        books,
        book_count: books.length,
        total_spins: roulette_spins.length,
        last_spin_at:
          roulette_spins.length > 0 ? roulette_spins[roulette_spins.length - 1].spun_at : undefined,
        roulette_spins,
      }
    },
    enabled: !!user?.id && !!rouletteId,
  })

  const {
    data: history = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['roulette-history', rouletteId, user?.id],
    queryFn: async (): Promise<RouletteSpinHistory[]> => {
      if (!user?.id || !rouletteId) throw new Error('Missing parameters')

      const { data, error } = await supabase
        .from('roulette_spins')
        .select(`
          id,
          selected_book_id,
          spin_duration,
          spun_at,
          books (
            id,
            title,
            author,
            cover_url,
            status,
            rating
          )
        `)
        .eq('roulette_id', rouletteId)
        .order('spun_at', { ascending: false })

      if (error) throw error
      return data.map((item) => ({
        ...item,
        books: (Array.isArray(item.books) ? item.books[0] : item.books) as Book,
      })) as RouletteSpinHistory[]
    },
    enabled: !!user?.id && !!rouletteId,
  })

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString))
  }

  // Sort by date (most recent first)
  const sortedHistory = [...history].sort((a, b) => {
    return new Date(b.spun_at).getTime() - new Date(a.spun_at).getTime()
  })

  // Calculate statistics
  const getRouletteStats = () => {
    if (!roulette) return null

    const spunBookIds = new Set(roulette.roulette_spins?.map((spin) => spin.selected_book_id) || [])
    const availableBooks = roulette.book_count - spunBookIds.size
    const totalSpins = history.length

    // Calculate this week's spins
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    const thisWeekSpins = history.filter((h) => new Date(h.spun_at) > oneWeekAgo).length

    return {
      totalSpins,
      thisWeekSpins,
      availableBooks,
      isCompleted: availableBooks === 0 && roulette.book_count > 0,
    }
  }

  const stats = getRouletteStats()

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="text-error mb-4">
            <svg
              className="mx-auto h-12 w-12 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-medium">{t('roulette.errorLoadingHistory')}</h3>
            <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
          </div>
          <Button onClick={onBackToRoulette} variant="outline">
            {t('roulette.backToRoulettes')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                📊 {t('roulette.progress')}
              </h1>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                {t('roulette.progressDescription')}
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              {/* Spin Button */}
              {roulette && onSpinRoulette && stats && stats.availableBooks > 0 && (
                <Button
                  onClick={() => onSpinRoulette(roulette)}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  🎲 {t('roulette.spin')}
                </Button>
              )}

              <Button onClick={onBackToRoulette} variant="outline">
                ← {t('roulette.backToRoulettes')}
              </Button>
            </div>
          </div>

          {/* Roulette Info */}
          {roulette && (
            <div className="bg-card rounded-lg border border-border p-4 sm:p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-xl"
                  style={{ backgroundColor: `${roulette.color}40` }}
                >
                  {roulette.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold text-foreground truncate">
                    {roulette.name}
                  </h2>
                  {roulette.description && (
                    <p className="text-muted-foreground mt-1 text-sm sm:text-base break-words">
                      {roulette.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Statistics */}
              {stats && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{stats.totalSpins}</div>
                    <div className="text-sm text-muted-foreground">{t('roulette.totalSpins')}</div>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <div className="text-2xl font-bold text-success">{stats.thisWeekSpins}</div>
                    <div className="text-sm text-muted-foreground">{t('roulette.thisWeek')}</div>
                  </div>
                </div>
              )}

              {/* Progress */}
              {stats && (
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-foreground">
                      {t('roulette.progress')}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {roulette.book_count - stats.availableBooks}/{roulette.book_count}{' '}
                      {t('roulette.books').toLowerCase()}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        stats.isCompleted ? 'bg-success' : 'bg-primary'
                      }`}
                      style={{
                        width: `${((roulette.book_count - stats.availableBooks) / roulette.book_count) * 100}%`,
                      }}
                    ></div>
                  </div>
                  {stats.isCompleted && (
                    <div className="text-sm text-success mt-1 flex items-center gap-1">
                      ✓ {t('roulette.completed')}
                    </div>
                  )}
                </div>
              )}

              {/* Books in Roulette */}
              {roulette.books && roulette.books.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('roulette.books')} ({roulette.books.length})
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {roulette.books.map((book) => {
                      const isSpun = roulette.roulette_spins?.some(
                        (spin) => spin.selected_book_id === book.id
                      )
                      return (
                        <div
                          key={book.id}
                          className="relative group"
                          title={`${book.title} - ${book.author}${isSpun ? ` (${t('roulette.alreadySelected')})` : ''}`}
                        >
                          <div
                            className={`aspect-[3/4] bg-muted rounded overflow-hidden transition-all duration-200 ${
                              isSpun ? 'grayscale opacity-50' : ''
                            }`}
                          >
                            {book.cover_url ? (
                              <img
                                src={book.cover_url}
                                alt={book.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div
                                className={`w-full h-full flex items-center justify-center text-xs ${
                                  isSpun ? 'text-muted-foreground/50' : 'text-muted-foreground'
                                }`}
                              >
                                📖
                              </div>
                            )}
                          </div>
                          <div
                            className={`mt-1 text-xs truncate transition-colors duration-200 ${
                              isSpun ? 'text-muted-foreground/50' : 'text-muted-foreground'
                            }`}
                            title={book.title}
                          >
                            {book.title}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="bg-card rounded-lg border border-border p-6">
            <div className="h-6 w-32 bg-muted rounded animate-pulse mb-4"></div>
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((item) => (
                <div
                  key={`history-loading-item-${item}`}
                  className="h-16 bg-muted rounded animate-pulse"
                ></div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-card rounded-lg border border-border">
            <div className="p-4 sm:p-6 pb-4">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-foreground">{t('roulette.history')}</h2>
              </div>

              {sortedHistory.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-4">
                    <div className="text-4xl mb-2">📜</div>
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t('roulette.noHistory')}
                    </h3>
                    <p className="text-muted-foreground">{t('roulette.noHistoryDescription')}</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedHistory.map((result) => (
                    <div
                      key={result.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Book Cover */}
                        <div className="w-12 h-16 bg-muted rounded flex-shrink-0 overflow-hidden">
                          {result.books.cover_url ? (
                            <img
                              src={result.books.cover_url}
                              alt={result.books.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                              📖
                            </div>
                          )}
                        </div>

                        {/* Book Info */}
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-foreground mb-1 truncate">
                            {result.books.title}
                          </h3>
                          <div className="text-sm text-muted-foreground truncate">
                            {result.books.author}
                          </div>
                          {result.books.rating && (
                            <div className="text-sm text-amber mt-1">
                              {'⭐'.repeat(result.books.rating)}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Spin Info */}
                      <div className="text-right flex-shrink-0 sm:text-right text-left">
                        <div className="text-sm font-medium text-foreground">
                          {formatDate(result.spun_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
